import { useQuery } from "@tanstack/react-query";
import { profileApi } from "@/api/profile";
import { useOptionalAuth } from "@/hooks/useAuth";
import { sendPreviewKeys } from "./sendPreviewKeys";

/**
 * Who the outbound email will appear to be from, and where a reply lands.
 *
 * <p>Read from the acting user's own profile, because that is where the server
 * resolves it — the same `StaffSignatureComposer.ResolveOutbound` call the send
 * itself makes. Composing `signatureEmail ?? email` here would be right today
 * and free to drift tomorrow, and a send preview that stops matching what is
 * sent is worse than no preview at all.</p>
 *
 * <p>🔴 <b>A failed or pending read yields `undefined`, never a guess.</b> The
 * preview then omits the identity block rather than filling it with the login
 * email — a plausible wrong address beside a Send button is exactly the kind of
 * detail somebody would rely on.</p>
 *
 * <p>🔴 <b>`useOptionalAuth`, not `useAuth`.</b> `renderWithProviders` supplies a
 * QueryClient and a ToastProvider but NO AuthProvider, so the throwing variant
 * would make this shared hook a hard dependency on every consumer's test setup —
 * three panels today, and every surface that later renders one. The identity is
 * already an enhancement that degrades to nothing, so having no auth context is
 * simply another way of not knowing it.</p>
 */
export function useSenderIdentity() {
  const auth = useOptionalAuth();
  const userId = auth?.user?.id ?? "";

  const profile = useQuery({
    queryKey: sendPreviewKeys.senderIdentity(userId),
    queryFn: () => profileApi.get(userId),
    enabled: Boolean(userId),
  });

  const p = profile.data;
  // `undefined` when we do not know, which the preview renders as "we do not
  // know" rather than as an address. `replyToEmail` being null is a DIFFERENT
  // answer — it means this user genuinely has no address on file, and replies
  // will reach Phoenix.
  return p
    ? { replyToEmail: p.replyToEmail ?? null, replyToDisplayName: p.replyToDisplayName ?? null }
    : undefined;
}

export type SenderIdentity = NonNullable<ReturnType<typeof useSenderIdentity>>;
